import { getClientId } from '@framework/app';
import { useHandler, useMousetrap } from '@framework/hooks';
import { PerfProfiler } from '@framework/utils';
import type { ViewModelOperationLogSender } from '@model-framework/action-log';
import type { CommandManager } from '@model-framework/command';
import type { LinkColor, LinkLineStyle, LinkMarkStyle } from '@model-framework/link';
import type {
  AnnotationLinkId,
  AnnotationLinkStyle,
  FileContainerId,
  GroupId,
  ModelCommentThreadId,
  ModelId,
  NodeId,
  StickyZoneId,
  ViewId,
} from 'balus-common';
import type { UserPublicProfile } from '@user/PublicProfile';
import type { StickyModelContentsOperation } from '@view-model/adapter';
import { ElementDragManager } from '@view-model/adapter';
import type { ApplicationClipboardPayload } from '@view-model/application/clipboard';
import type { WindowEventManager } from '@view-model/application/shortcuts';
import { KeyboardShortcuts } from '@view-model/application/shortcuts';
import type { LinkKey, LinkableTargetKey, ModelElementId, StickyZoneKey } from '@view-model/domain/key';
import type { StickyModel } from '@view-model/domain/model/StickyModel';
import type { StickyModelElement } from '@view-model/domain/model';
import { StickyModelElementCollection } from '@view-model/domain/model';
import type { ViewEntity } from '@view-model/domain/view/ViewEntity';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import type { Size } from '@view-model/models/common/basic';
import type { Rect } from '@view-model/models/common/basic/Rect';
import { Point, SizeSet } from '@view-model/models/common/basic';
import type { ThemeColor } from '@view-model/models/common/color';
import type { TextAlign } from '@view-model/models/common/text-align';
import { LinkerCanvasView } from '@view-model/models/common/components/LinkCreator';
import { DescriptionPanelCollectionView } from '@view-model/models/sticky/DescriptionPanel';
import { FileContainerCollectionView } from '@view-model/models/sticky/FileContainer/components/FileContainerCollectionView';
import { AnnotationLinkCollectionView } from '@view-model/models/sticky/AnnotationLink/components/AnnotationLinkCollectionView';
import { AnnotationLinkCreator } from '@view-model/models/sticky/AnnotationLink/components/AnnotationLinkCreator';
import type { AnnotationLinkOrigin } from '@view-model/models/sticky/AnnotationLink/domain/AnnotationLinkOrigin';
import { findAnnotationLinkTarget } from '@view-model/models/sticky/AnnotationLink/adapter/findAnnotationLinkTarget';
import { LinkableTargetContainer } from '@view-model/models/sticky/StickyNodeView/components/LinkableTargetContainer';
import { RectShape } from '@model-framework/shape';
import type { LinkBend } from '@view-model/models/sticky/StickyLink/domain/vo/LinkBend';
import type { CreatingModelComment } from '@view-model/models/sticky/ModelComment';
import { CreatingModelCommentWrapper, ModelCommentList } from '@view-model/models/sticky/ModelComment';
import { LinkPlacementFactory } from '@view-model/models/sticky/StickyLink/domain/vo/LinkPlacementFactory';
import { LinkerOperation, LinkEntity, StickyLink } from '@view-model/models/sticky/StickyLink';
import { ElementDescriptionView } from '@view-model/models/sticky/ElementDescription';
import { EllipseTarget } from '@model-framework/ui';
import type { NodeFontSize } from '@view-model/models/sticky/StickyNodeView';
import { StickyNodeView } from '@view-model/models/sticky/StickyNodeView/StickyNodeView';
import { NodeCollection, StickyNode } from '@view-model/models/sticky/StickyNodeView';
import { StickyZoneView } from '@view-model/models/sticky/StickyZoneView/StickyZoneView';
import {
  StickyZone,
  StickyZoneContextProvider,
  StickyZoneCollection,
  StickyZoneShape,
} from '@view-model/models/sticky/StickyZoneView';
import type { StickyZoneTitlePosition } from '@view-model/models/sticky/StickyZoneView';
import { MultiSelectionView } from '@view-model/models/sticky/ui';
import { ModelCanvas, RectSelectorView } from '@view-model/ui/components/Model';
import type { ViewSelectionDragStrategy } from '@view-model/ui/components/Model/ModelCanvas/ModelCanvas';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IStickyModelContentsViewHandlers } from './IStickyModelContentsViewHandlers';
import { NullHandlers } from './NullHandlers';
import { ReadonlyHandlers } from './ReadonlyHandlers';
import { WritableHandlers } from './WritableHandlers';
import { LinkerState } from '@view-model/models/common/components/LinkCreator/LinkerState';
import { MultiSelectionMode } from '@user/pages/ViewModelPage';
import { EditingUserRepository } from '@model-framework/text/editing-user';
import { useSelectedItems } from '../../../../models/sticky/client-selected-items/useSelectedItems';
import { ModelLayout } from '@view-model/models/sticky/layout';
import type { DragContext } from '@model-framework/ui';
import type { AlignType } from '@view-model/models/sticky/ui/MultiSelectionView/components';
import { StickyModelContentsLoader } from '@view-model/ui/components/Model/StickyModelContentsView/StickyModelContentsLoader';
import { useAtomValue } from 'jotai';
import { stickyModelContentsAtomFamily } from '@view-model/adapter/stickyModelContentsAtomFamily';
import { ElementDescriptionTarget } from '@view-model/models/sticky/ElementDescription/domain/ElementDescriptionTarget';
import { useUserOperationModes } from '@user/pages/ViewModelPage/UserOperationModesProvider';
import { viewTitleBarAtom } from '../../View/viewTitleBarAtom';
import type { DragFileTarget, DragFileTargetActions } from '@model-framework/drag-file/DragFileArea';
import { DragFileArea } from '@model-framework/drag-file/DragFileArea';
import type { GetGuardProps } from '@model-framework/drag-file/useDragFileGuard';
import { useEditingDescriptionPanel } from '@view-model/models/sticky/DescriptionPanel/hook/EditingDescriptionPanelContext';
import type { Position, PositionDelta, SizeType } from '@view-model/models/common/types/ui';
import { useRightClickMenuOpenState } from '@user/pages/ViewModelPage/useRightClickMenuOpenState';
import { RightClickMenu, RightClickMenuItem } from '@framework/ui/elements/RightClickMenu';
import { StickyZoneBorderView } from '@view-model/models/sticky/StickyZoneBorderView';
import { useGroupLicensePermissions } from '@group/License';
import { useUserType } from '@framework/auth/authStatus';
type Props = {
  readonly: boolean;
  ownerGroupId: GroupId;
  currentUserProfile: UserPublicProfile;
  viewModel: ViewModelEntity;
  view: ViewEntity;
  model: StickyModel;
  isSelected: boolean;
  showNodeCreatedUser: boolean;
  canvasSize: SizeType;
  onSelectSingleView(viewId?: ViewId): void;
  onToggleSelectedViews(): void;
  onDeselectAllViews(): void;
  setMultiSelectionMode(multiSelectionMode: MultiSelectionMode): void;
  onCopyView(): void;
  windowEventManager: WindowEventManager;
  multiSelectionMode: MultiSelectionMode;
  commandManager: CommandManager;
  logSender: ViewModelOperationLogSender;
  onDeleteView(): void;
  onClickViewModelLink(e: React.MouseEvent): void;
  modelCommentThreadId: string | null;
  operation: StickyModelContentsOperation;
  creatingComment: CreatingModelComment | undefined;
  onCreatingCommentAdd(): void;
  onCreatingCommentDrag(dx: number, dy: number): void;
  onCreatingCommentDragEnd(): void;
  onCreatingCommentCancel(): void;
  onCreatingCommentSubmit(creatingComment: CreatingModelComment): void;
  onCreatingCommentAddByRightClick(modelId: ModelId, position: Point): void;
  onZoneToView(sourceView: ViewEntity, operation: StickyModelContentsOperation): void;
  getTransformedVisibleAreaCenterPoint: (viewRect: Rect) => Point;
  viewRect: Rect;
  getViewRectOperations(): Record<ViewId, { view: ViewEntity; rect?: Rect; operation?: StickyModelContentsOperation }>;
  getGuardProps: GetGuardProps;
  dispatch: React.Dispatch<DragFileTargetActions>;
  dragFileTargetState: DragFileTarget;
  onClickView(e: MouseEvent): void;
};

const annotationLinkOriginLogParams = ({ kind, id }: AnnotationLinkOrigin) =>
  kind === 'FileContainer' ? { fileContainerId: id } : { panelId: id };

type State = {
  linkerState: LinkerState<LinkableTargetKey> | null;
  annotationLinkSource: AnnotationLinkOrigin | null;
  annotationLinkTarget: AnnotationLinkOrigin | null;
  startEditTargetId: NodeId | null;
  selectionRect: Rect | null;
  dropTargetZones: StickyZone[];
  commentSizeSet: SizeSet;
  replacingLinkKey: LinkKey | null;
};

type DragFileAreaProps = React.ComponentProps<typeof DragFileArea>;

// ビュータイトルのフォントサイズはズームのたびに変わる。StickyModelContentsView 本体で購読すると、
// 使うのはファイルをドラッグ中の DragFileArea だけなのに、ズームのたびにビュー全体が再レンダーされる
const DragFileAreaWithTitleFontSize: React.FC<Omit<DragFileAreaProps, 'fontSize'>> = (
  props: Omit<DragFileAreaProps, 'fontSize'>
) => {
  const {
    title: { fontSize },
  } = useAtomValue(viewTitleBarAtom);

  return <DragFileArea {...props} fontSize={fontSize} />;
};

const isSameZones = (a: StickyZone[], b: StickyZone[]): boolean =>
  a.length === b.length && a.every((zone, i) => zone.key.isEqual(b[i].key));

const StickyModelContentsViewFn = (props: Props) => {
  const { t } = useTranslation();
  const licensePermissions = useGroupLicensePermissions();
  const isAnonymousUser = useUserType() === 'anonymous';
  const propsRef = useRef<Props>(props);
  propsRef.current = props;
  const { onSelectSingleView, modelCommentThreadId, view, model, onDeselectAllViews, getGuardProps, dispatch } = props;

  const { userOperationMode } = useUserOperationModes();
  const { editingPanelId } = useEditingDescriptionPanel();

  const [state, setState] = useState<State>({
    linkerState: null,
    annotationLinkSource: null,
    annotationLinkTarget: null,
    startEditTargetId: null,
    dropTargetZones: [],
    selectionRect: null,
    commentSizeSet: new SizeSet(),
    replacingLinkKey: null,
  });
  const { activeRightClickMenuIds, setActiveRightClickMenuIds } = useRightClickMenuOpenState();
  const [canvasPopupMenuPosition, setCanvasPopupMenuPosition] = useState<Position | null>(null);

  const selectedItems = useSelectedItems({ clientId: getClientId(), modelId: model.id });

  const stickyModelContents = useAtomValue(stickyModelContentsAtomFamily(model.id));
  const {
    panelContents,
    fileContainerContents,
    annotationLinks,
    commentContents,
    displayOrderTree,
    stickyZonePositions,
    stickyZones,
    stickyNodes,
    links,
  } = stickyModelContents;

  const annotationLinkTarget = state.linkerState ? state.annotationLinkTarget : null;
  const annotationLinkTargetRect =
    annotationLinkTarget?.kind === 'DescriptionPanel'
      ? panelContents.getRect(annotationLinkTarget.id)
      : annotationLinkTarget && fileContainerContents.getRect(annotationLinkTarget.id);

  const nodePositions = useMemo(() => new NodeCollection(stickyNodes).positionSet(), [stickyNodes]);

  const shapeMap = useMemo(
    () => new NodeCollection(stickyNodes).shapeMap().merge(new StickyZoneCollection(stickyZones).shapeMap()),
    [stickyNodes, stickyZones]
  );

  const editingUserRepositoryRef = useRef<EditingUserRepository>(new EditingUserRepository(props.viewModel.id));

  useMousetrap(
    KeyboardShortcuts.escape,
    () => {
      if (state.linkerState || state.replacingLinkKey) {
        setState((prev) => {
          if (prev.replacingLinkKey) {
            editingUserRepositoryRef.current.delete(prev.replacingLinkKey).then();
          }
          return {
            ...prev,
            linkerState: null,
            replacingLinkKey: null,
            annotationLinkSource: null,
            annotationLinkTarget: null,
          };
        });
      }

      setActiveRightClickMenuIds([]);
      handlersRef.current.handleAllElementsDeselect();
      onDeselectAllViews();
      props.setMultiSelectionMode(MultiSelectionMode.offMode);
    },
    'keydown'
  );

  useEffect(() => {
    const unsubscribe = props.windowEventManager.listenOnWindowActive((windowActive: boolean) => {
      if (!windowActive) {
        setState((prev) => {
          if (prev.replacingLinkKey) {
            editingUserRepositoryRef.current.delete(prev.replacingLinkKey).then();
          }
          if (prev.linkerState || prev.replacingLinkKey) {
            return {
              ...prev,
              linkerState: null,
              replacingLinkKey: null,
              annotationLinkSource: null,
              annotationLinkTarget: null,
            };
          }
          return prev;
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [props.windowEventManager]);

  const handlersRef = useRef<IStickyModelContentsViewHandlers>(new NullHandlers());

  // URL パラメータで指定されたスレッドをローカル state で管理する。
  // RTDB（selectOnly）を使うと React StrictMode の cleanup で選択が消えるため、
  // ローカル state で "表示強制" フラグを持ち、RTDB に依存しないようにする。
  const [urlOpenThreadId, setUrlOpenThreadId] = useState<ModelCommentThreadId | null>(
    modelCommentThreadId as ModelCommentThreadId | null
  );

  // modelCommentThreadId の変化に追従して urlOpenThreadId を更新する。
  // useState の初期値だけでは、同一コンポーネントがマウントされたまま URL の threadId が
  // 変わった場合（アプリ内遷移・履歴操作）に新しいスレッドが開かないため effect で同期する。
  useEffect(() => {
    setUrlOpenThreadId(modelCommentThreadId as ModelCommentThreadId | null);
    if (modelCommentThreadId) {
      onSelectSingleView();
    }
  }, [onSelectSingleView, modelCommentThreadId, view.id]);

  const dragManagerRef = useRef<ElementDragManager | null>(null);

  if (!dragManagerRef.current) {
    dragManagerRef.current = new ElementDragManager(
      props.viewModel.id,
      props.model.id,
      props.model.key,
      props.view.id,
      props.commandManager,
      props.operation,
      props.getViewRectOperations,
      props.onSelectSingleView
    );
  }

  useEffect(() => {
    if (!props.isSelected) {
      handlersRef.current = new NullHandlers();
      return;
    }

    if (props.readonly) {
      const { operation } = propsRef.current;
      handlersRef.current = new ReadonlyHandlers(operation);
    } else {
      const { onDeleteView, operation } = propsRef.current;
      handlersRef.current = new WritableHandlers(operation, onDeleteView);
    }
  }, [props.isSelected, props.readonly]);

  useEffect(() => {
    // 離脱する際には選択を解除する
    const operation = props.operation;
    return () => {
      operation.deleteMySelection();
    };
  }, [props.operation]);

  const multiSelectedRect = selectedItems.isMultiSelected()
    ? props.model.getBoundsOf(selectedItems.getSelectedItems())
    : null;

  const handleStartEditNode = useHandler(() => {
    const selectedIds = selectedItems.getSelectedItems();
    // 単一のNodeが選択されているときのみ実行
    if (selectedIds.length !== 1) {
      return;
    }
    setState((prev) => ({ ...prev, startEditTargetId: selectedIds[0] }));
  });

  // 矢印の方向にあるノードに選択を移動する
  const handleMoveSelection = useHandler((direction: 'up' | 'left' | 'right' | 'down') => {
    // ビュー選択モードの場合は何もしない
    if (props.multiSelectionMode.isMultiViewsSelectionMode) {
      return;
    }

    // 単一のNodeが選択されているときのみ実行
    const selectedIds = selectedItems.getSelectedItems();
    if (selectedIds.length !== 1) {
      return;
    }

    // 現在選択しているノードとその位置を得る
    const selectedNode = stickyNodes.find((node) => {
      return node.id === selectedIds[0];
    });

    // ノードが見つからない、もしくは選択している要素がノードじゃない場合は何もしない
    if (!selectedNode) {
      return;
    }

    // 範囲外のノードを除外
    const targetNodes = stickyNodes.filter(
      (() => {
        switch (direction) {
          case 'up':
            return (n: StickyNode) => n.position.y < selectedNode.position.y;
          case 'down':
            return (n: StickyNode) => n.position.y > selectedNode.position.y;
          case 'left':
            return (n: StickyNode) => n.position.x < selectedNode.position.x;
          case 'right':
            return (n: StickyNode) => n.position.x > selectedNode.position.x;
        }
      })()
    );

    const minFactor = Math.tan(Math.PI * (20 / 180)); // 一定の角度以内であれば距離の重み付けは均一にする
    const sortedNodes = targetNodes
      .map((node) => {
        const xDiff = Math.abs(node.position.x - selectedNode.position.x);
        const yDiff = Math.abs(node.position.y - selectedNode.position.y);
        const distance = selectedNode.position.toPoint().distance(node.position.toPoint());

        // 相対的な位置から重み付けの値を計算する
        const factor = Math.max(minFactor, ['up', 'down'].includes(direction) ? xDiff / distance : yDiff / distance);

        return {
          node,
          // 実際の距離ではなく概念的な距離を計算する
          distance: factor * distance,
        };
      })
      .sort((left, right) => {
        if (left.distance === right.distance) {
          // 距離が同じ場合は、結果が不定にならないようにする
          return left.node.compareByDisplayOrder(right.node);
        }
        return left.distance < right.distance ? -1 : 1;
      });

    if (sortedNodes[0]) {
      // 距離の一番近いノードを探して選択する
      props.operation.selectOnly(sortedNodes[0].node.id);
    }
  });

  useMousetrap(
    KeyboardShortcuts.delete,
    () => {
      // ドラッグ中であればショートカットによる削除操作を受け付けない
      if (draggingRef.current) return;
      handlersRef.current.handleDeleteSelectedElements();
    },
    'keydown'
  );

  useMousetrap(
    KeyboardShortcuts.up,
    (e) => {
      if (e.ctrlKey || e.metaKey) {
        handleMoveSelection('up');
      } else {
        handlersRef.current.handleArrowKeys(0, -ModelLayout.GridSize);
      }
    },
    'keydown'
  );

  useMousetrap(
    KeyboardShortcuts.down,
    (e) => {
      if (e.ctrlKey || e.metaKey) {
        handleMoveSelection('down');
      } else {
        handlersRef.current.handleArrowKeys(0, ModelLayout.GridSize);
      }
    },
    'keydown'
  );

  useMousetrap(
    KeyboardShortcuts.left,
    (e) => {
      if (e.ctrlKey || e.metaKey) {
        handleMoveSelection('left');
      } else {
        handlersRef.current.handleArrowKeys(-ModelLayout.GridSize, 0);
      }
    },
    'keydown'
  );

  useMousetrap(
    KeyboardShortcuts.right,
    (e) => {
      if (e.ctrlKey || e.metaKey) {
        handleMoveSelection('right');
      } else {
        handlersRef.current.handleArrowKeys(ModelLayout.GridSize, 0);
      }
    },
    'keydown'
  );

  useMousetrap(
    KeyboardShortcuts.selectAll,
    () => {
      handlersRef.current.handleAllElementsSelect();
    },
    'keydown'
  );

  useMousetrap(
    KeyboardShortcuts.enter,
    () => {
      handleStartEditNode();
    },
    'keydown'
  );

  // タブキーで選択中のノードの右にノードを追加する
  useMousetrap(
    KeyboardShortcuts.tab,
    () => {
      if (props.multiSelectionMode.isMultiViewsSelectionMode || props.readonly) {
        return;
      }

      const selectedIds = selectedItems.getSelectedItems();
      if (selectedIds.length !== 1) {
        return;
      }

      const selectedNode = stickyNodes.find((node) => {
        return node.id === selectedIds[0];
      });

      if (!selectedNode) {
        return;
      }

      const nodeSize = StickyNode.size();

      const createNodePosition = (i: number): Position => {
        const mx = ModelLayout.GridSize * 2;
        const my = ModelLayout.GridSize * 2;
        const position = { x: selectedNode.position.x + mx + nodeSize.width, y: selectedNode.position.y };

        if (i > 0) {
          if (i % 2 === 1) {
            position.y -= (Math.floor(i / 2) + 1) * (my + nodeSize.height);
          } else {
            position.y += Math.floor(i / 2) * (my + nodeSize.height);
          }
        }

        return position;
      };

      // 選択中のノードの右側にあるノードだけ考慮
      const targetNodes = stickyNodes.filter((node) => node.position.x > selectedNode.position.x);
      const nodePosition = (() => {
        for (let i = 0; ; i++) {
          const position = createNodePosition(i);

          const isIn = (targetPosition: Position) => {
            return (
              targetPosition.x <= position.x + nodeSize.width &&
              targetPosition.x >= position.x &&
              targetPosition.y <= position.y + nodeSize.height &&
              targetPosition.y >= position.y
            );
          };

          // すでにあるノードと位置がかぶる場合は別の場所を探す
          if (
            targetNodes.some(
              (node) =>
                isIn(node.position) ||
                isIn(node.position.add(nodeSize.width, 0)) ||
                isIn(node.position.add(0, nodeSize.height)) ||
                isIn(node.position.add(nodeSize.width, nodeSize.height))
            )
          ) {
            continue;
          }

          return position;
        }
      })();

      const [node, link] = props.operation.createLinkedNode(Point.fromPosition(nodePosition), selectedNode.key);
      setState((prev) => ({ ...prev, startEditTargetId: node.id })); // 対象要素をテキスト編集状態にする

      props.logSender('sticky:node:create', {
        nodeId: node.id,
        cause: 'tab',
      });

      props.logSender('sticky:link:create', {
        nodeId: link.id,
        cause: 'tab',
      });
    },
    'keydown'
  );

  const handlePasteClipboard = useHandler(async (payload: ApplicationClipboardPayload) => {
    const { getTransformedVisibleAreaCenterPoint, viewRect, operation } = props;
    const createPoint = operation.getCreatePoint(getTransformedVisibleAreaCenterPoint(viewRect));
    // 編集中のパネルの上でペースト操作された場合はペーストを受け付けない
    if (
      editingPanelId &&
      panelContents.filterByPositionSelection(createPoint.toPosition()).panels.allIds().includes(editingPanelId)
    )
      return;
    handlersRef.current.handlePasteClipboardPayload(payload, createPoint, {
      deniedMessage: isAnonymousUser
        ? t('canvas.fileContainer.signInRequired')
        : licensePermissions?.fileUpload === false
          ? t('common.label.licenseRequired')
          : null,
      onFileContainerCreated: (fileContainerId) => {
        props.logSender('sticky:file_container:create', { fileContainerId, cause: 'paste' });
      },
    });
  });

  const handleDuplicateSelectedElements = useHandler(async () => {
    const { getTransformedVisibleAreaCenterPoint, viewRect, operation } = props;
    const createPoint = operation.getCreatePoint(getTransformedVisibleAreaCenterPoint(viewRect));
    handlersRef.current.handleDuplicateSelectedElements(createPoint);
  });

  useEffect(() => {
    const windowEventManager = props.windowEventManager;
    const modelKey = props.model.key;

    windowEventManager.onModelContentsMounted(modelKey, {
      onCopy: () => handlersRef.current.handleCopySelectedElements(),
      onCut: () => {
        // ドラッグ中であればショートカットによる切り取り操作を受け付けない
        if (draggingRef.current) return;
        handlersRef.current.handleCutSelectedElements();
      },
      onPaste: handlePasteClipboard,
      onDuplicate: () => handleDuplicateSelectedElements(),
    });

    return () => {
      windowEventManager.onModelContentsWillUnmount(modelKey);
    };
  }, [props.windowEventManager, props.model.key, handlePasteClipboard, handleDuplicateSelectedElements]);

  useEffect(() => {
    if (!props.isSelected && selectedItems.isSomeSelected()) {
      props.operation.deselectAll();
      setState((prev) => {
        if (prev.replacingLinkKey) {
          editingUserRepositoryRef.current.delete(prev.replacingLinkKey).then();
        }
        return {
          ...prev,
          linkerState: null,
          replacingLinkKey: null,
          annotationLinkSource: null,
          annotationLinkTarget: null,
        };
      });
    }
  }, [props.isSelected, props.operation, selectedItems]);

  const handleClickCanvas = useHandler((e: MouseEvent) => {
    const { isSelected: isViewSelected, operation, setMultiSelectionMode, onClickView } = props;

    // ビューが選択されていて、かつ、モデル要素が選択されている状態でキャンバスがクリックされたときには、
    // モデル要素の選択状態を解除、リンク作成モードを解除する
    if (isViewSelected && selectedItems.isSomeSelected()) {
      operation.deselectAll();
      setMultiSelectionMode(MultiSelectionMode.offMode);
      setState((prev) => {
        if (prev.replacingLinkKey) {
          editingUserRepositoryRef.current.delete(prev.replacingLinkKey).then();
        }
        return {
          ...prev,
          linkerState: null,
          replacingLinkKey: null,
          annotationLinkSource: null,
          annotationLinkTarget: null,
        };
      });
      // キャンバスクリックで要素選択をすべて解除したタイミングで、右クリックメニューの対象elementIdもクリアしておかないと
      // メニューだけが残り続けてUI状態が不整合になるため、ここで明示的に閉じる
      setActiveRightClickMenuIds([]);
      return;
    }

    onClickView(e);
  });

  const handleDelete = useHandler(() => {
    handlersRef.current.handleDeleteSelectedElements();
  });

  const handleDeleteFileContainer = useHandler((id: FileContainerId) => {
    handlersRef.current.handleDeleteFileContainer(id);
  });

  const handleResizeFileContainer = useHandler((id: FileContainerId, positionDelta: PositionDelta, size: Size) => {
    handlersRef.current.handleResizeFileContainer(id, positionDelta, size);
  });

  const handleCreateNodeByDblClick = useHandler((position: Position) => {
    if (props.readonly) return;

    props.onSelectSingleView(); // ビューを選択して
    const nodeId = props.operation.createNodeAtClickedPosition(Point.fromPosition(position));
    setState((prev) => ({ ...prev, startEditTargetId: nodeId })); // 対象要素をテキスト編集状態にする

    props.logSender('sticky:node:create', {
      nodeId,
      cause: 'dblclick_canvas',
    });
  });

  const handleCreateNodeByRightClick = useHandler(() => {
    if (props.readonly || !canvasPopupMenuPosition) return;

    props.onSelectSingleView(); // ビューを選択して
    const nodeId = props.operation.createNodeAtClickedPosition(Point.fromPosition(canvasPopupMenuPosition));
    setState((prev) => ({ ...prev, startEditTargetId: nodeId })); // 対象要素をテキスト編集状態にする

    props.logSender('sticky:node:create', {
      nodeId,
      cause: 'rightclick_canvas',
    });
  });

  const handleCreateZoneByRightClick = useHandler(() => {
    if (props.readonly || !canvasPopupMenuPosition) return;

    props.onSelectSingleView(); // ビューを選択して
    const zoneId = props.operation.createZone(Point.fromPosition(canvasPopupMenuPosition));

    props.logSender('sticky:zone:create', {
      zoneId,
      cause: 'rightclick_canvas',
    });
  });

  const handleAddDescriptionPanelByRightClick = useHandler(() => {
    if (props.readonly || !canvasPopupMenuPosition) return;

    props.onSelectSingleView(); // ビューを選択して
    const panelId = props.operation.addDescriptionPanel(Point.fromPosition(canvasPopupMenuPosition));

    props.logSender('sticky:description_panel:create', {
      panelId,
      cause: 'rightclick_canvas',
    });
  });

  const handleAddCommentByRightClick = useHandler(() => {
    if (props.readonly || !canvasPopupMenuPosition) return;

    props.onSelectSingleView(); // ビューを選択して
    props.onCreatingCommentAddByRightClick(props.model.id, Point.fromPosition(canvasPopupMenuPosition));
  });

  const handleElementClick = useHandler((id: ModelElementId, e: React.MouseEvent<SVGGElement>) => {
    const { multiSelectionMode, operation, onSelectSingleView, onToggleSelectedViews, setMultiSelectionMode } = props;

    if (e.shiftKey || e.ctrlKey || e.metaKey) {
      if (multiSelectionMode.isMultiElementsSelectionMode) {
        operation.toggleSelection(id);
        onSelectSingleView();
      } else if (multiSelectionMode.isMultiViewsSelectionMode) {
        onToggleSelectedViews();
      } else {
        operation.toggleSelection(id);
        onSelectSingleView();
        setMultiSelectionMode(MultiSelectionMode.multiElementsSelectionMode);
      }
    } else {
      operation.selectOnly(id);
      onSelectSingleView();
      setMultiSelectionMode(MultiSelectionMode.offMode);
    }
  });

  const handleCommentClose = useHandler((id: ModelCommentThreadId) => {
    // URL パラメータによる強制表示中に toggleSelection すると「未選択→選択」になってしまうため、
    // 選択済みの場合のみ選択解除する。
    if (selectedItems.isSelected(id)) {
      props.operation.toggleSelection(id);
    }
    if (id === urlOpenThreadId) {
      setUrlOpenThreadId(null);
    }
  });

  const handleAddNodeToZone = useHandler((position: Position, zoneKey: StickyZoneKey) => {
    if (props.readonly) return;
    props.onSelectSingleView(); // ビューを選択して

    const nodeId = props.operation.createNodeToZone(Point.fromPosition(position), zoneKey);

    setState((prev) => ({ ...prev, startEditTargetId: nodeId })); // 対象要素をテキスト編集状態にする

    props.logSender('sticky:node:create', {
      nodeId,
      zoneId: zoneKey.id,
      cause: 'dblclick_zone',
    });
  });

  const handleNameEditStartedOnMount = useHandler(() => {
    setState((prev) => ({ ...prev, startEditTargetId: null }));
  });

  const handleUpdateCommentSize = useHandler((id: ModelCommentThreadId, newSize: Size) => {
    setState((prev) => ({
      ...prev,
      commentSizeSet: prev.commentSizeSet.set(id, newSize),
    }));
  });

  const draggingRef = useRef<boolean>(false);
  const handleDragStart = useHandler((id: ModelElementId, e: React.MouseEvent<SVGGElement>) => {
    const { multiSelectionMode, onSelectSingleView, onToggleSelectedViews, operation, setMultiSelectionMode } = props;

    if (props.readonly) {
      return;
    }

    if (multiSelectionMode.isMultiViewsSelectionMode) {
      onToggleSelectedViews();
      return;
    }

    // 複数選択時に要素をクリックした際にドラッグに化けた時の処理
    // 複数選択キーを押したまま選択対象の要素をドラッグされた場合、選択対象の要素をドラッグする複数要素の中に含めて移動させる
    let isMultiSelected = false;
    if (e.shiftKey || e.ctrlKey || e.metaKey) {
      operation.toggleSelection(id);
      isMultiSelected = true;
      setMultiSelectionMode(MultiSelectionMode.multiElementsSelectionMode);
    }

    draggingRef.current = true;
    onSelectSingleView();
    dragManagerRef.current?.onDragStart(id, isMultiSelected);

    const dropTargetZones = props.operation.findDropTargetZones();
    setState((prev) => ({ ...prev, dropTargetZones }));
  });

  const handleDrag = useHandler((context: DragContext) => {
    if (props.multiSelectionMode.isMultiViewsSelectionMode || props.readonly) {
      return;
    }

    if (draggingRef.current) {
      dragManagerRef.current?.onDrag(context);
      const dropTargetZones = props.operation.findDropTargetZones();
      setState((prev) => (isSameZones(prev.dropTargetZones, dropTargetZones) ? prev : { ...prev, dropTargetZones }));
    }
  });

  const handleDragEnd = useHandler((context: DragContext) => {
    if (props.multiSelectionMode.isMultiViewsSelectionMode || props.readonly) {
      return;
    }

    if (draggingRef.current) {
      dragManagerRef.current?.onDragEnd(context);
      draggingRef.current = false;
    }

    setState((prev) => ({ ...prev, dropTargetZones: [] }));
  });

  const handleEditStart = useHandler((id: ModelElementId) => {
    if (props.readonly) return;

    props.operation.selectOnly(id);
    props.setMultiSelectionMode(MultiSelectionMode.offMode);
  });

  const handleRectSelection = useHandler((selectionRect: Rect) => {
    if (!props.isSelected) {
      // 矩形選択しようとしているビューが非選択状態ならば、選択状態に変更して
      props.onSelectSingleView();
    }

    setState((prev) => ({ ...prev, selectionRect }));
  });

  const handleRectSelectionEnd = useHandler((selectionRect: Rect, zoneId?: StickyZoneId) => {
    const { operation } = props;
    operation.selectElementsByRect({ selectionRect, zoneId, commentSizeSet: state.commentSizeSet });

    setState((prev) => ({ ...prev, selectionRect: null }));
  });

  const handleCreateLinkerStart = useHandler((key: LinkableTargetKey, startPosition: Position) => {
    const linkerState = LinkerState.creatingStart<LinkableTargetKey>(key, startPosition);
    setState((prev) => ({ ...prev, linkerState, annotationLinkSource: null, annotationLinkTarget: null }));
  });

  const handleCreateAnnotationLinkerStart = useHandler((from: AnnotationLinkOrigin, startPosition: Position) => {
    // 起点のファイルコンテナ・説明パネルは付箋・ゾーンのキーで表せないため、起点を空にした状態で線を引き始め、起点は別に持つ
    const linkerState = new LinkerState<LinkableTargetKey>({
      source: null,
      sourcePosition: startPosition,
      target: null,
      targetPosition: startPosition,
      linkerType: 'create',
    });
    setState((prev) => ({
      ...prev,
      linkerState,
      annotationLinkSource: from,
      annotationLinkTarget: null,
    }));
  });

  const handleReplaceLinkerStart = useHandler((linkerState: LinkerState<LinkableTargetKey>, linkKey: LinkKey) => {
    setState((prev) => ({
      ...prev,
      linkerState,
      replacingLinkKey: linkKey,
      annotationLinkSource: null,
      annotationLinkTarget: null,
    }));
  });

  const handleLinkerMove = useHandler((currentPosition: Position) => {
    const linkableElement = props.operation.findForegroundElementByPosition(currentPosition);

    if (!state.linkerState) {
      return;
    }

    const annotationLinkTarget = findAnnotationLinkTarget(
      state.linkerState,
      linkableElement,
      () => props.operation.findFileContainerIdByPosition(currentPosition),
      () => props.operation.findDescriptionPanelIdByPosition(currentPosition)
    );
    const elementKey = annotationLinkTarget
      ? null
      : LinkerOperation.getLinkableElementKey({
          linkerState: state.linkerState,
          linkableElement,
          displayOrderTree,
        });
    const linkerState = state.linkerState.move(elementKey, currentPosition);
    setState((prev) => ({ ...prev, linkerState, annotationLinkTarget }));
  });

  const handleLinkerEnd = useHandler((currentPosition: Position) => {
    const linkableElement = props.operation.findForegroundElementByPosition(currentPosition);

    if (!state.linkerState) {
      console.warn(
        'LinkerOperation.handleLinkerFinished() is called multiple times after single handleLinkStart(). ' +
          'It may cause an unpredictable problem.'
      );
      return;
    }

    const annotationLinkTarget = findAnnotationLinkTarget(
      state.linkerState,
      linkableElement,
      () => props.operation.findFileContainerIdByPosition(currentPosition),
      () => props.operation.findDescriptionPanelIdByPosition(currentPosition)
    );
    const targetKey = annotationLinkTarget
      ? null
      : LinkerOperation.getLinkableElementKey({
          linkableElement,
          displayOrderTree,
          linkerState: state.linkerState,
        });
    const linkerState = state.linkerState.move(targetKey, currentPosition);
    const { annotationLinkSource } = state;

    if (!propsRef.current.readonly) {
      if (annotationLinkSource) {
        if (linkerState.target) {
          const annotationLinkId = propsRef.current.operation.createAnnotationLink(
            annotationLinkSource,
            linkerState.target
          );
          propsRef.current.logSender('sticky:annotation_link:create', {
            ...annotationLinkOriginLogParams(annotationLinkSource),
            linkId: annotationLinkId,
            cause: 'linker',
          });
        }
      } else if (annotationLinkTarget && linkerState.source) {
        const annotationLinkId = propsRef.current.operation.createAnnotationLink(
          annotationLinkTarget,
          linkerState.source
        );
        propsRef.current.logSender('sticky:annotation_link:create', {
          ...annotationLinkOriginLogParams(annotationLinkTarget),
          linkId: annotationLinkId,
          cause: 'linker',
        });
      } else if (linkerState.source && linkerState.target) {
        if (linkerState.isCreating()) {
          const linkKey = propsRef.current.operation.createLink(linkerState.source, linkerState.target);

          if (!linkKey) {
            return;
          }

          propsRef.current.logSender('sticky:link:create', {
            linkId: linkKey.id,
            cause: 'linker',
          });
        } else {
          const { replacingLinkKey } = state;
          if (!replacingLinkKey) return;

          const currentLink = links.find((link) => link.key.isEqual(replacingLinkKey));
          if (!currentLink) return;

          propsRef.current.operation.replaceLink(
            currentLink.key,
            { sourceKey: currentLink.from, targetKey: currentLink.to },
            { sourceKey: linkerState.source, targetKey: linkerState.target }
          );
        }
      }
    }

    setState((prev) => ({
      ...prev,
      replacingLinkKey: null,
      linkerState: null,
      annotationLinkSource: null,
      annotationLinkTarget: null,
    }));
  });

  const handleAnnotationLinkStyleSelected = useHandler((id: AnnotationLinkId, style: AnnotationLinkStyle) => {
    props.operation.changeAnnotationLinkStyle(id, style);
  });

  const handleReverseAnnotationLink = useHandler((id: AnnotationLinkId) => {
    props.operation.reverseAnnotationLink(id);
  });

  const handleAnnotationLinkBendChanged = useHandler((id: AnnotationLinkId, bend: LinkBend) => {
    props.operation.changeAnnotationLinkBend(id, bend);
  });

  const handleNodeZoneThemeColorMenuSelected = useHandler((themeColor: ThemeColor) => {
    props.operation.changeSelectedNodeZoneThemeColor(themeColor);
  });

  const handleNodeZoneFontSizeMenuSelected = useHandler((fontSize: NodeFontSize) => {
    props.operation.changeSelectedNodeZoneFontSize(fontSize);
  });

  const handleToggleSelectedNodeFontStrikethrough = useHandler((fontStrikethrough: boolean) => {
    props.operation.toggleSelectedNodeFontStrikethrough(fontStrikethrough);
  });

  const handleNodeZoneTextAlignMenuSelected = useHandler((textAlign: TextAlign) => {
    props.operation.changeSelectedNodeZoneTextAlign(textAlign);
  });

  const handleZoneShapeMenuSelected = useHandler((shape: StickyZoneShape) => {
    props.operation.changeSelectedZoneShape(shape);
  });

  const handleZoneTitlePositionMenuSelected = useHandler((titlePosition: StickyZoneTitlePosition) => {
    props.operation.changeSelectedZoneTitlePosition(titlePosition);
  });

  const handleMultiSelectGroupSelectedElements = useHandler(async () => {
    const zoneId = await props.operation.groupSelectedElements();
    props.logSender('sticky:zone:create', {
      zoneId,
      cause: 'grouping',
    });
  });

  const handleLinkLineStyleMenuSelected = useHandler((lineStyle: LinkLineStyle) => {
    props.operation.changeSelectedLinkLineStyles(lineStyle);
  });

  const handleLinkMarkStyleMenuSelected = useHandler((markStyle: LinkMarkStyle) => {
    props.operation.changeSelectedLinkMarkStyles(markStyle);
  });

  const handleReverseLinks = useHandler(() => {
    props.operation.changeReverseLinks();
  });

  const handleRemoveMultiSelectedLinks = useHandler(() => {
    props.operation.removeMultiSelectedLinks();
  });

  const handleLinkColorMenuSelected = useHandler((linkColor: LinkColor) => {
    props.operation.changeSelectedLinkColors(linkColor);
  });

  const handleAlignSelected = useHandler((alignType: AlignType, interval: number) => {
    props.operation.alignSelectedNodes(alignType, interval);
  });

  const handleRightClickCanvas = useHandler((mousePosition: Position) => {
    // 右クリックメニューはビューが選択されているときにのみ表示する
    if (!props.isSelected || props.readonly) return;
    setActiveRightClickMenuIds([props.view.id]);
    setCanvasPopupMenuPosition(mousePosition);
  });

  const elementCollection = useMemo(
    () => new StickyModelElementCollection(stickyNodes, links, stickyZones, stickyZonePositions),
    [stickyNodes, links, stickyZones, stickyZonePositions]
  );
  const orderedElements = useMemo(
    () => elementCollection.backToFront(displayOrderTree, selectedItems),
    [elementCollection, displayOrderTree, selectedItems]
  );
  const linkPlacementFactory = useMemo(
    () => new LinkPlacementFactory(nodePositions, stickyZonePositions, shapeMap),
    [nodePositions, stickyZonePositions, shapeMap]
  );

  const handleZoneToView = useHandler(() => {
    props.onZoneToView(props.view, props.operation);
  });

  const isDropTargetView = useCallback((): boolean => {
    const viewId = props.view.id;
    return props.dragFileTargetState.viewId === viewId;
  }, [props]);

  // 閲覧のみ・匿名のユーザーが付箋などの上にファイルをドロップすると、受け止める要素が無いためブラウザがファイルを開く可能性がある。
  // そうした操作はほぼ起きないため受容している。
  const isUndroppableDropTarget = (props.readonly || isAnonymousUser) && isDropTargetView();
  useEffect(() => {
    if (isUndroppableDropTarget) {
      dispatch({ type: 'Exit', payload: { viewId: props.view.id } });
    }
  }, [isUndroppableDropTarget, dispatch, props.view.id]);

  const hookDragCanvas = useHandler(() => {
    props.setMultiSelectionMode(MultiSelectionMode.multiElementsSelectionMode);
  });

  const hookDragEndCanvas = useHandler(() => {
    props.setMultiSelectionMode(MultiSelectionMode.offMode);
  });

  const checkDraggingMultiSelectionDisabled = useCallback(
    (e: MouseEvent): boolean => {
      if (userOperationMode === 'map') return !(e.shiftKey || e.ctrlKey || e.metaKey);

      return false;
    },
    [userOperationMode]
  );

  // 修飾キー押下時のキャンバスドラッグ戦略を決定する
  // editモード: 修飾キー押下時はドラッグを完全抑制し、クリック（ビュー選択トグル）として扱う
  // mapモード: Shift+ドラッグが要素の矩形選択として機能するため、距離閾値でクリック/ドラッグを判定
  const getDragStrategyForViewSelection = useCallback(
    (e: MouseEvent): ViewSelectionDragStrategy => {
      const isModifierHeld = e.shiftKey || e.ctrlKey || e.metaKey;
      if (!isModifierHeld) return 'none';
      if (userOperationMode === 'map') return 'threshold';
      return 'suppress';
    },
    [userOperationMode]
  );

  // ゾーンの描画データを事前計算
  const zoneRenderData = useMemo(() => {
    // orderedElementsからゾーンのみを抽出
    const zones = orderedElements.filter((element): element is StickyZone => element instanceof StickyZone);

    return zones
      .map((zone) => {
        const position = stickyZonePositions.find(zone.id);
        const shape = shapeMap.get(zone.id);
        if (!position || !shape) return null;

        const isSelected = selectedItems.isSelected(zone.id);
        const isMultiSelected = isSelected && selectedItems.isMultiSelected();
        const showMenu = !props.readonly && isSelected && selectedItems.isSingleSelected();
        const isLinkableTarget =
          !!state.linkerState?.target?.isEqual(zone.key) || !!state.linkerState?.source?.isEqual(zone.key);

        return {
          zone,
          position,
          shape,
          isSelected,
          isMultiSelected,
          showMenu,
          isLinkableTarget,
        };
      })
      .filter((data): data is NonNullable<typeof data> => data !== null);
  }, [orderedElements, stickyZonePositions, shapeMap, selectedItems, props.readonly, state.linkerState]);

  return (
    <>
      <StickyModelContentsLoader model={props.model} viewModelId={props.viewModel.id} />
      <g>
        <ModelCanvas
          viewId={props.view.id}
          size={props.canvasSize}
          onClickCanvas={handleClickCanvas}
          onDblClickCanvas={handleCreateNodeByDblClick}
          onRightClickCanvas={handleRightClickCanvas}
          onRectSelection={handleRectSelection}
          onRectSelectionEnd={handleRectSelectionEnd}
          checkDraggingMultiSelectionDisabled={checkDraggingMultiSelectionDisabled}
          onSelectSingleView={onSelectSingleView}
          viewThemeColor={view.themeColor}
          getGuardProps={getGuardProps}
          hookDragCanvas={hookDragCanvas}
          hookDragEndCanvas={hookDragEndCanvas}
          multiSelectionMode={props.multiSelectionMode}
          getDragStrategyForViewSelection={getDragStrategyForViewSelection}
        />

        {/* 説明パネルの一覧 */}
        <DescriptionPanelCollectionView
          viewModelId={props.viewModel.id}
          modelId={props.model.id}
          panelContents={panelContents}
          selectedItems={selectedItems}
          readonly={props.readonly}
          onClick={handleElementClick}
          onDragStart={handleDragStart}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
        />

        <FileContainerCollectionView
          groupId={props.ownerGroupId}
          viewModelId={props.viewModel.id}
          modelId={props.model.id}
          fileContainerContents={fileContainerContents}
          selectedItems={selectedItems}
          showCreatedUser={props.showNodeCreatedUser}
          readonly={props.readonly}
          onClick={handleElementClick}
          onDragStart={handleDragStart}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
          onDelete={handleDeleteFileContainer}
          onResize={handleResizeFileContainer}
        />

        {/* 各要素を包む g[data-model-element-id] は、AIモデリングのエフェクトが要素をDOMから引くための目印。
            DraggableSVGGElement が親要素の getScreenCTM でドラッグ座標を求めているため、
            この g に transform を持たせるとドラッグ位置がずれる。出現エフェクトが与える transform は、
            要素が見えるようになるまでに原点へ戻り切るぶんだけ許容している。 */}
        {/* ゾーンのコンテンツを描画 */}
        <PerfProfiler id={`view:${props.view.id}:zones`}>
          {zoneRenderData.map(({ zone, position, shape, isSelected, isMultiSelected, showMenu }) => (
            <g key={zone.key.toString()} data-model-element-id={zone.id}>
              <StickyZoneContextProvider
                currentUserProfile={props.currentUserProfile}
                viewModelId={props.viewModel.id}
                modelId={props.model.id}
                zoneId={zone.id}
                operation={props.operation}
              >
                <ElementDescriptionView
                  viewModelId={props.viewModel.id}
                  modelId={props.model.id}
                  target={ElementDescriptionTarget.Zone}
                  elementId={zone.id}
                  elementRect={zone.getRect(position)}
                  readonly={props.readonly}
                  targetCalloutTarget={
                    zone.style.shape === StickyZoneShape.Circle
                      ? new EllipseTarget(zone.getRect(position), ModelLayout.GridSize)
                      : undefined
                  }
                />
                <StickyZoneView
                  key={zone.key.toString()}
                  viewModelId={props.viewModel.id}
                  view={props.view}
                  stickyZone={zone}
                  position={position}
                  currentUserProfile={props.currentUserProfile}
                  readonly={props.readonly}
                  isHoverSelected={state.dropTargetZones.some((dropTargetZone) => dropTargetZone.key.isEqual(zone.key))}
                  isSelected={isSelected}
                  isMultiSelected={isMultiSelected}
                  checkDraggingMultiSelectionDisabled={checkDraggingMultiSelectionDisabled}
                  // 選択されておらず、かつ要素の複数選択モード時でもなく、かつスライドモードでもない場合にはドラッグハンドルを表示する
                  showHoveringDragHandle={
                    userOperationMode === 'map' && !isSelected && !props.multiSelectionMode.isMultiElementsSelectionMode
                  }
                  shape={shape}
                  showMenu={showMenu}
                  onClick={handleElementClick}
                  onDragStart={handleDragStart}
                  onDrag={handleDrag}
                  onDragEnd={handleDragEnd}
                  onEditStart={handleEditStart}
                  onCreateNodeInZone={handleAddNodeToZone}
                  onLinkerStart={handleCreateLinkerStart}
                  onLinkerMove={handleLinkerMove}
                  onLinkerEnd={handleLinkerEnd}
                  onClickViewModelLink={props.onClickViewModelLink}
                  onDelete={handleDelete}
                  onGroupSelectedZone={handleMultiSelectGroupSelectedElements}
                  onZoneToView={handleZoneToView}
                  onFontSizeSelected={handleNodeZoneFontSizeMenuSelected}
                  onThemeColorSelected={handleNodeZoneThemeColorMenuSelected}
                  onTextAlignSelected={handleNodeZoneTextAlignMenuSelected}
                  onZoneShapeSelected={handleZoneShapeMenuSelected}
                  onZoneTitlePositionSelected={handleZoneTitlePositionMenuSelected}
                  onRectSelection={handleRectSelection}
                  onRectSelectionEnd={handleRectSelectionEnd}
                />
              </StickyZoneContextProvider>
            </g>
          ))}

          {/* ゾーンのボーダーを描画 */}
          {zoneRenderData.map(({ zone, position, shape, isSelected, isMultiSelected, isLinkableTarget }) => (
            <g key={zone.key.toString()} data-model-element-id={zone.id}>
              <StickyZoneContextProvider
                currentUserProfile={props.currentUserProfile}
                viewModelId={props.viewModel.id}
                modelId={props.model.id}
                zoneId={zone.id}
                operation={props.operation}
              >
                <StickyZoneBorderView
                  key={zone.id.toString()}
                  isMultiSelected={isMultiSelected}
                  readonly={props.readonly}
                  stickyZone={zone}
                  position={position}
                  shape={shape}
                  isHoverSelected={state.dropTargetZones.some((dropTargetZone) => dropTargetZone.key.isEqual(zone.key))}
                  isSelected={isSelected}
                  isLinkableTarget={isLinkableTarget}
                  modelId={props.model.id}
                  showCreatedUser={props.showNodeCreatedUser}
                />
              </StickyZoneContextProvider>
            </g>
          ))}
        </PerfProfiler>

        {/* ゾーン以外の残りの要素を描画する */}
        <PerfProfiler id={`view:${props.view.id}:nodes+links`}>
          {orderedElements.map((element: StickyModelElement) => {
            if (element instanceof StickyNode) {
              const node = element;

              // positions に座標が含まれていない場合には、描画をスキップする
              // (positions が変化したときには再描画されるので、ここでは単純にスキップすれば良い)
              const position = nodePositions.find(node.id);
              const nodeShape = shapeMap.get(node.id);
              if (!position || !nodeShape) return null;

              const isSelected = selectedItems.isSelected(node.id);
              const showMenu = isSelected && selectedItems.isSingleSelected();
              const isMultiSelected = isSelected && selectedItems.isMultiSelected();
              const isLinkableTarget =
                !!state.linkerState?.target?.isEqual(node.key) || !!state.linkerState?.source?.isEqual(node.key);
              const baseColor = elementCollection.getNodeBaseColor(node.id, displayOrderTree);

              return (
                <g key={node.key.toString()} data-model-element-id={node.id}>
                  <ElementDescriptionView
                    viewModelId={props.viewModel.id}
                    modelId={props.model.id}
                    target={ElementDescriptionTarget.Node}
                    elementId={node.id}
                    elementRect={node.getRect()}
                    readonly={props.readonly}
                  />
                  <StickyNodeView
                    key={node.key.toString()}
                    readonly={props.readonly}
                    viewModelId={props.viewModel.id}
                    modelId={props.model.id}
                    node={node}
                    position={position}
                    currentUserProfile={props.currentUserProfile}
                    nodeShape={nodeShape}
                    isSelected={isSelected}
                    baseColor={baseColor}
                    showMenu={showMenu}
                    isMultiSelected={isMultiSelected}
                    isLinkableTarget={isLinkableTarget}
                    showNodeCreatedUser={props.showNodeCreatedUser}
                    onClick={handleElementClick}
                    onDragStart={handleDragStart}
                    onDrag={handleDrag}
                    onDragEnd={handleDragEnd}
                    onEditStart={handleEditStart}
                    startEditOnMount={state.startEditTargetId === node.id}
                    onNameEditStartedOnMount={handleNameEditStartedOnMount}
                    onLinkerStart={handleCreateLinkerStart}
                    onLinkerMove={handleLinkerMove}
                    onLinkerEnd={handleLinkerEnd}
                    onClickViewModelLink={props.onClickViewModelLink}
                    onGroupSelectedNode={handleMultiSelectGroupSelectedElements}
                    onDelete={handleDelete}
                    onFontSizeSelected={handleNodeZoneFontSizeMenuSelected}
                    onToggleFontStrikethrough={handleToggleSelectedNodeFontStrikethrough}
                    onThemeColorSelected={handleNodeZoneThemeColorMenuSelected}
                    onTextAlignSelected={handleNodeZoneTextAlignMenuSelected}
                  />
                </g>
              );
            }

            if (element instanceof LinkEntity) {
              const link = element;
              const isSelected = selectedItems.isSelected(link.id);
              // リンクが1つだけ選択されている場合は操作メニュー付きとして描画する。
              const showMenu = isSelected && selectedItems.isSingleSelected();

              const placement = linkPlacementFactory.linkPlacementOf(link);
              if (!placement) return null;

              // リンクの接続されているいずれかのノードが選択されているか否か
              const isRelatedNodeSelected =
                selectedItems.isSelected(link.fromId) || selectedItems.isSelected(link.toId);

              const isReplacing = state.replacingLinkKey ? link.key.isEqual(state.replacingLinkKey) : false;
              return (
                <g key={link.key.toString()} data-model-element-id={link.id}>
                  <StickyLink
                    readonly={props.readonly}
                    viewModel={props.viewModel}
                    modelKey={props.model.key}
                    link={link}
                    isSelected={isSelected}
                    isRelatedNodeSelected={isRelatedNodeSelected}
                    placement={placement}
                    showMenu={showMenu}
                    onClick={handleElementClick}
                    onLineStyleSelected={handleLinkLineStyleMenuSelected}
                    onMarkStyleSelected={handleLinkMarkStyleMenuSelected}
                    onColorSelected={handleLinkColorMenuSelected}
                    onReplaceStart={handleReplaceLinkerStart}
                    onReplaceMove={handleLinkerMove}
                    onReplaceEnd={handleLinkerEnd}
                    isReplacing={isReplacing}
                  />
                </g>
              );
            }
          })}
        </PerfProfiler>
        <AnnotationLinkCollectionView
          viewModelId={props.viewModel.id}
          annotationLinks={annotationLinks}
          fileContainerContents={fileContainerContents}
          panelContents={panelContents}
          nodePositions={nodePositions}
          zonePositions={stickyZonePositions}
          shapeMap={shapeMap}
          selectedItems={selectedItems}
          readonly={props.readonly}
          onClick={handleElementClick}
          onStyleSelected={handleAnnotationLinkStyleSelected}
          onReverse={handleReverseAnnotationLink}
          onBendChanged={handleAnnotationLinkBendChanged}
          onDelete={handleDelete}
        />
        {annotationLinkTargetRect && (
          <g transform={annotationLinkTargetRect.topLeft().toSVGTranslate()}>
            <LinkableTargetContainer
              shape={new RectShape({ width: annotationLinkTargetRect.width, height: annotationLinkTargetRect.height })}
              margin={12}
            />
          </g>
        )}
        <AnnotationLinkCreator
          fileContainerContents={fileContainerContents}
          panelContents={panelContents}
          selectedItems={selectedItems}
          readonly={props.readonly}
          onLinkerStart={handleCreateAnnotationLinkerStart}
          onLinkerMove={handleLinkerMove}
          onLinkerEnd={handleLinkerEnd}
        />
        {/* 複数要素を選択しているときに表示する外接する矩形領域 */}
        {multiSelectedRect && (
          <MultiSelectionView
            rect={multiSelectedRect}
            readonly={props.readonly}
            isNodeSelected={elementCollection.isNodeSelected(selectedItems)}
            isMultipleNodeSelected={elementCollection.isMultipleNodeSelected(selectedItems)}
            isLinkSelected={elementCollection.isLinkSelected(selectedItems)}
            isZoneSelected={elementCollection.isZoneSelected(selectedItems)}
            currentFontSize={elementCollection.getSelectedFontSize(selectedItems)}
            currentThemeColor={elementCollection.getSelectedThemeColor(selectedItems)}
            currentTextAlign={elementCollection.getSelectedTextAlign(selectedItems)}
            currentZoneShape={elementCollection.getSelectedZoneShape(selectedItems)}
            currentLinkLineStyle={elementCollection.getSelectedLinkLineStyle(selectedItems)}
            currentLinkMarkStyle={elementCollection.getSelectedLinkMarkStyle(selectedItems)}
            currentLinkColor={elementCollection.getSelectedLinkColor(selectedItems)}
            selectedItems={selectedItems.getSelectedItems()}
            onFontSizeSelected={handleNodeZoneFontSizeMenuSelected}
            onToggleFontStrikethrough={handleToggleSelectedNodeFontStrikethrough}
            onThemeColorSelected={handleNodeZoneThemeColorMenuSelected}
            onTextAlignSelected={handleNodeZoneTextAlignMenuSelected}
            onZoneShapeSelected={handleZoneShapeMenuSelected}
            onGroupSelectedElements={handleMultiSelectGroupSelectedElements}
            onLinkLineStyleSelected={handleLinkLineStyleMenuSelected}
            onLinkMarkStyleSelected={handleLinkMarkStyleMenuSelected}
            onReverseLinks={handleReverseLinks}
            onRemoveLinks={handleRemoveMultiSelectedLinks}
            onLinkColorSelected={handleLinkColorMenuSelected}
            onAlignSelected={handleAlignSelected}
            onDragStart={handleDragStart}
            onDrag={handleDrag}
            onDragEnd={handleDragEnd}
          />
        )}

        {/* リンク作成中には一番手前に透明なキャンバスを重ねる */}
        {state.linkerState && (state.linkerState.source || state.linkerState.target || state.annotationLinkSource) && (
          <LinkerCanvasView
            canvasSize={props.canvasSize}
            linkerState={state.linkerState}
            onLinkerMove={handleLinkerMove}
            onLinkerEnd={handleLinkerEnd}
            viewModelId={props.viewModel.id}
            replacingLinkKey={state.replacingLinkKey}
          />
        )}

        {/* モデルコメントの一覧 */}
        <ModelCommentList
          viewModelId={props.viewModel.id}
          modelId={props.model.id}
          commentContents={commentContents}
          onUpdateCommentSize={handleUpdateCommentSize}
          selectedItems={selectedItems}
          urlOpenThreadId={urlOpenThreadId}
          readonly={props.readonly}
          onDragStart={handleDragStart}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
          onClick={handleElementClick}
          onClose={handleCommentClose}
        />

        {/* 下書き途中(未投稿)のモデルコメント */}
        <CreatingModelCommentWrapper
          comment={props.creatingComment}
          onUpdateCommentPosition={props.onCreatingCommentDrag}
          onUpdateCommentPositionDragEnd={props.onCreatingCommentDragEnd}
          onCancel={props.onCreatingCommentCancel}
          onSubmit={props.onCreatingCommentSubmit}
        />

        {/* Shiftキー + ドラッグで選択中の矩形領域 */}
        {state.selectionRect && <RectSelectorView rect={state.selectionRect} />}

        {isDropTargetView() && !props.readonly && !isAnonymousUser && (
          <DragFileAreaWithTitleFontSize
            viewId={props.view.id}
            viewRect={props.viewRect}
            size={view.size}
            dispatch={dispatch}
            logSender={props.logSender}
          />
        )}
        <RightClickMenu
          isOpen={activeRightClickMenuIds.length === 1 && activeRightClickMenuIds[0] === props.view.id}
          mousePosition={canvasPopupMenuPosition}
        >
          <RightClickMenuItem
            onClick={handleCreateNodeByRightClick}
            title={t('canvas.stickyModel.rightClickMenu.createNode')}
          />
          <RightClickMenuItem
            onClick={handleCreateZoneByRightClick}
            title={t('canvas.stickyModel.rightClickMenu.createZone')}
          />
          <RightClickMenuItem
            onClick={handleAddDescriptionPanelByRightClick}
            title={t('canvas.stickyModel.rightClickMenu.createPanel')}
          />
          <RightClickMenuItem
            onClick={handleAddCommentByRightClick}
            title={t('canvas.stickyModel.rightClickMenu.createComment')}
          />
        </RightClickMenu>
      </g>
    </>
  );
};

export const StickyModelContentsView = memo(StickyModelContentsViewFn);
